import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  NgModule,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, TitleStrategy } from '@angular/router';
import {
  MsalBroadcastService,
  MsalGuard,
  MsalInterceptor,
  MsalModule,
  MsalService,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  PublicClientApplication,
} from '@azure/msal-browser';
import { NdsModule } from '@netural/nds';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { SEU_ENVIRONMENT } from '@seu-environment';
import { getMSALRedirectUrl } from '@seu-helpers';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routing';

import { EnvInterceptor, HttpRequestInterceptor } from '@seu-interceptors';
import { DirectivesModule } from './shared/directives/directives.module';
import { CustomTitleResolver } from './shared/resolver/custom-title.resolver';

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http);
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    DirectivesModule,
    TranslateModule.forRoot({
      defaultLanguage: 'de-DE',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    NdsModule.forRoot({
      basePath: 'designsystem',
      storyblok: {
        version: 'draft',
        accessToken: 'zbJLNPSDQonSFT8gbmQD7Att',
      },
      logLevel: 'TRACE',
    }),
    MsalModule.forRoot(
      new PublicClientApplication({
        // MSAL Configuration
        auth: {
          clientId: SEU_ENVIRONMENT.msal.clientId,
          authority: SEU_ENVIRONMENT.msal.authority,
          redirectUri: getMSALRedirectUrl(SEU_ENVIRONMENT),
        },
        cache: {
          cacheLocation: BrowserCacheLocation.LocalStorage,
          storeAuthStateInCookie: false, // set to true for IE 11
        },
        system: {
          loggerOptions: {
            loggerCallback: () => {},
            piiLoggingEnabled: false,
          },
        },
      }),
      {
        interactionType: InteractionType.Redirect,
        authRequest: {
          scopes: ['user.read'],
        },
      },
      {
        interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
        protectedResourceMap: new Map<string, string[]>([
          ['https://graph.microsoft.com/v1.0/me', ['user.read']],
          [
            `${SEU_ENVIRONMENT.apiUrl}/`,
            [`${SEU_ENVIRONMENT.apiUrl}/general.access`],
          ],
        ]),
      }
    ),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EnvInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpRequestInterceptor,
      multi: true,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    {
      provide: LOCALE_ID,
      useValue: 'de-DE',
    },
    {
      provide: TitleStrategy,
      useClass: CustomTitleResolver,
    },
    provideHttpClient(withInterceptorsFromDi()),
    SEU_ENVIRONMENT.sentryEnabled
      ? {
          provide: ErrorHandler,
          useValue: Sentry.createErrorHandler({
            showDialog: false,
          }),
        }
      : [],
    SEU_ENVIRONMENT.sentryEnabled
      ? {
          provide: Sentry.TraceService,
          deps: [Router],
        }
      : [],
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
})
export class AppModule {}
